import { useEffect, useState, useCallback } from "react";
import { Loader2, RefreshCw, Wifi, WifiOff, RotateCcw, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";

export default function Sheets() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const canTest = ["super_admin", "admin"].includes(user?.role);

  const load = useCallback(() => {
    api.get("/admin/sheets/status").then((r) => setStatus(r.data)).catch(() => setStatus(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/sheets/test");
      toast.success(`Connected. Worksheets: ${data.worksheets.join(", ")}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setTesting(false);
    }
  };

  const syncPending = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/sheets/sync-pending");
      toast.success(`Retried ${data.retried}: ${data.synced} synced, ${data.failed} failed`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSyncing(false);
    }
  };

  const retryOne = async (id) => {
    try {
      const { data } = await api.post(`/admin/sheets/retry/${id}`);
      data.ok ? toast.success("Synced to Google Sheets") : toast.error("Sync still failing — check credentials and sheet access");
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  if (status === null) return <div className="py-24 flex justify-center"><Loader2 className="animate-spin text-brand-red" size={28} /></div>;
  if (status === false) return <div className="py-24 text-center text-brand-muted">Unable to load integration status.</div>;

  const cards = [
    { label: "Connection", value: status.configured ? "Configured" : "Not Configured", icon: status.configured ? Wifi : WifiOff, testid: "sheets-connection", ok: status.configured },
    { label: "Total Synced Leads", value: status.synced, icon: CheckCircle2, testid: "sheets-synced", ok: true },
    { label: "Pending Records", value: status.pending, icon: RefreshCw, testid: "sheets-pending", ok: true },
    { label: "Failed Records", value: status.failed, icon: AlertCircle, testid: "sheets-failed", ok: status.failed === 0 },
  ];

  return (
    <div data-testid="admin-sheets-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-brand-dark">Google Sheets Integration</h1>
          <p className="text-sm text-brand-muted mt-0.5">Leads sync server-side to the BharatBest lead-management spreadsheet. The CRM database remains the source of truth.</p>
        </div>
        <div className="flex gap-2">
          {canTest && (
            <button onClick={test} disabled={testing} data-testid="sheets-test-btn" className="btn-outline !px-5 !py-2.5 !text-xs">
              {testing ? <Loader2 size={14} className="animate-spin" /> : <><Wifi size={14} /> Test Connection</>}
            </button>
          )}
          <button onClick={syncPending} disabled={syncing || !status.configured} data-testid="sheets-sync-pending-btn" className="btn-primary !px-5 !py-2.5 !text-xs">
            {syncing ? <Loader2 size={14} className="animate-spin" /> : <><RefreshCw size={14} /> Sync Pending / Retry Failed</>}
          </button>
        </div>
      </div>

      {!status.configured && (
        <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-800" data-testid="sheets-not-configured">
          <p className="font-bold">Google Sheets credentials are not configured yet.</p>
          <p className="mt-1 leading-relaxed">Leads continue to save safely to the CRM database with sync status "pending". Once the service-account credentials are added to the server environment (<code className="text-xs bg-amber-100 px-1 rounded">GOOGLE_SHEETS_SPREADSHEET_ID</code>, <code className="text-xs bg-amber-100 px-1 rounded">GOOGLE_SERVICE_ACCOUNT_EMAIL</code>, <code className="text-xs bg-amber-100 px-1 rounded">GOOGLE_PRIVATE_KEY</code>) and the spreadsheet is shared with the service account email, use "Sync Pending" to push all queued leads.</p>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl border border-black/5 p-4" data-testid={c.testid}>
            <c.icon size={17} className={c.ok ? "text-emerald-600" : "text-amber-600"} />
            <p className="mt-3 font-heading text-xl font-extrabold text-brand-dark">{c.value}</p>
            <p className="text-xs text-brand-muted font-semibold mt-0.5">{c.label}</p>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-black/5 p-5 mt-6">
        <div className="flex items-center justify-between">
          <h3 className="font-heading font-bold text-sm text-brand-dark">Last Successful Sync</h3>
          <span className="text-xs text-brand-muted" data-testid="sheets-last-sync">{status.last_sync_at ? new Date(status.last_sync_at).toLocaleString("en-IN") : "Never"}</span>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-black/5 mt-4" data-testid="sheets-errors">
        <div className="p-5 border-b border-black/5">
          <h3 className="font-heading font-bold text-sm text-brand-dark">Sync Errors</h3>
        </div>
        {status.errors.length === 0 ? (
          <p className="p-5 text-sm text-brand-muted">No failed records.</p>
        ) : (
          <div className="divide-y divide-black/5">
            {status.errors.map((e) => (
              <div key={e.id} className="p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`sheets-error-${e.lead_id}`}>
                <div className="min-w-0">
                  <p className="font-semibold text-sm text-brand-dark">{e.lead_id} · {e.name} <span className="text-xs font-normal text-brand-muted">({e.kind})</span></p>
                  <p className="text-xs text-red-600 mt-1 break-all">{e.error}</p>
                  <p className="text-xs text-brand-muted mt-0.5">Attempts: {e.attempts}</p>
                </div>
                <button onClick={() => retryOne(e.id)} data-testid={`sheets-retry-${e.lead_id}`} className="btn-outline !px-4 !py-2 !text-xs shrink-0">
                  <RotateCcw size={13} /> Retry
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
